import React from "react";
import { createPortal } from "react-dom";
import { Download, X } from "lucide-react";
import CustomDatePicker from "../../components/CustomDatePicker";

export default function FinanceExcelModal({
  isOpen,
  isMobile,
  onClose,
  position,
  onHeaderMouseDown,
  theme,
  currentDate,
  excelDateRange,
  setExcelDateRange,
  exportToExcel,
}) {
  if (!isOpen) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-[9999] flex ${isMobile ? "items-end" : "items-center justify-center p-4 pointer-events-none"}`}
      style={{ zIndex: 9999 }}
      onClick={() => isMobile && onClose()}
    >
      {isMobile && <div className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />}
      <div
        className={`bg-white/95 backdrop-blur-xl w-full relative z-10 flex flex-col shadow-2xl border border-white/50 pointer-events-auto ${isMobile
          ? "rounded-t-[2.5rem] max-h-[90vh] animate-slide-up-mobile p-6 pb-10"
          : "rounded-[2.5rem] max-w-lg"
          }`}
        style={!isMobile ? { transform: `translate(${position.x}px, ${position.y}px)` } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        {isMobile && (
          <div className="flex justify-center pt-0 pb-4 -mt-2">
            <div className="w-10 h-1 rounded-full bg-gray-300"></div>
          </div>
        )}

        <div
          className={`flex justify-between items-center ${isMobile ? "mb-6" : "px-8 py-6 border-b border-gray-100/50 bg-white/50 cursor-move"}`}
          onMouseDown={onHeaderMouseDown}
        >
          <div className="pointer-events-none select-none">
            <h3 className={`text-2xl font-black ${theme.text} flex items-center gap-3 font-do-hyeon`}>
              <img
                src="https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/Bar%20Chart.png"
                width={36}
                alt="icon"
                className="drop-shadow-md"
              />
              재정 내역 다운로드
            </h3>
            <p className="text-gray-400 text-sm font-bold mt-1 ml-1">원하는 기간의 재정 내역을 엑셀로 다운로드하세요.</p>
          </div>
          <button
            onClick={onClose}
            onMouseDown={(e) => e.stopPropagation()}
            className="text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 p-2 rounded-full transition pointer-events-auto"
          >
            <X size={22} />
          </button>
        </div>

        <div className={`${isMobile ? "overflow-y-auto flex-1" : "p-8 pt-6"} space-y-6`}>
          <div>
            <p className="text-xs font-bold text-gray-400 mb-3 ml-1 flex items-center gap-1.5">
              <span className="text-base">⚡</span> 빠른 선택
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => exportToExcel("month")}
                className="p-4 bg-[#F9F9F8] border-2 border-[#EAEAEA] rounded-2xl text-left transition-all group hover:shadow-md hover:border-gray-300 active:scale-[0.98]"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${theme.lightBg} rounded-xl flex items-center justify-center group-hover:scale-110 transition`}>
                    <span className="text-xl">📅</span>
                  </div>
                  <div>
                    <div className="font-black text-gray-800">{currentDate.getMonth() + 1}월 내역</div>
                    <div className="text-xs text-gray-400">이번 달 전체</div>
                  </div>
                </div>
              </button>
              <button
                onClick={() => exportToExcel("3months")}
                className="p-4 bg-[#F9F9F8] border-2 border-[#EAEAEA] rounded-2xl text-left transition-all group hover:shadow-md hover:border-gray-300 active:scale-[0.98]"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${theme.lightBg} rounded-xl flex items-center justify-center group-hover:scale-110 transition`}>
                    <span className="text-xl">🗓️</span>
                  </div>
                  <div>
                    <div className="font-black text-gray-800">최근 3개월</div>
                    <div className="text-xs text-gray-400">분기별 정산용</div>
                  </div>
                </div>
              </button>
              <button
                onClick={() => exportToExcel("year")}
                className="p-4 bg-[#F9F9F8] border-2 border-[#EAEAEA] rounded-2xl text-left transition-all group hover:shadow-md hover:border-gray-300 active:scale-[0.98]"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${theme.lightBg} rounded-xl flex items-center justify-center group-hover:scale-110 transition`}>
                    <span className="text-xl">📊</span>
                  </div>
                  <div>
                    <div className="font-black text-gray-800">{new Date().getFullYear()}년 전체</div>
                    <div className="text-xs text-gray-400">연간 결산용</div>
                  </div>
                </div>
              </button>
              <button
                onClick={() => exportToExcel("all")}
                className="p-4 bg-[#F9F9F8] border-2 border-[#EAEAEA] rounded-2xl text-left transition-all group hover:shadow-md hover:border-gray-300 active:scale-[0.98]"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${theme.lightBg} rounded-xl flex items-center justify-center group-hover:scale-110 transition`}>
                    <span className="text-xl">📁</span>
                  </div>
                  <div>
                    <div className="font-black text-gray-800">전체 내역</div>
                    <div className="text-xs text-gray-400">모든 기록 백업</div>
                  </div>
                </div>
              </button>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="h-px bg-gray-200 flex-1"></div>
            <span className="text-xs text-gray-400 font-bold">또는</span>
            <div className="h-px bg-gray-200 flex-1"></div>
          </div>

          <div className="bg-[#F9F9F8] border-2 border-[#EAEAEA] rounded-2xl p-5">
            <p className="text-xs font-bold text-gray-400 mb-4 flex items-center gap-1.5">
              <span className="text-base">📆</span> 기간 직접 선택
            </p>
            <div className="flex gap-3 mb-4">
              <div className="flex-1">
                <label className="text-xs font-bold text-gray-500 mb-1.5 block ml-1">시작일</label>
                <CustomDatePicker
                  theme={theme}
                  value={excelDateRange.start}
                  onChange={(date) => setExcelDateRange((prev) => ({ ...prev, start: date }))}
                  openDirection="up"
                  className="w-full"
                />
              </div>
              <div className="flex items-end pb-3 text-gray-300 font-bold text-lg">→</div>
              <div className="flex-1">
                <label className="text-xs font-bold text-gray-500 mb-1.5 block ml-1">종료일</label>
                <CustomDatePicker
                  theme={theme}
                  value={excelDateRange.end}
                  onChange={(date) => setExcelDateRange((prev) => ({ ...prev, end: date }))}
                  openDirection="up"
                  className="w-full"
                />
              </div>
            </div>
            <button
              onClick={() => exportToExcel("custom")}
              disabled={!excelDateRange.start || !excelDateRange.end}
              className={`w-full py-4 rounded-2xl font-black text-lg transition-all flex items-center justify-center gap-2 ${excelDateRange.start && excelDateRange.end
                ? `${theme.bg} text-white hover:opacity-90 shadow-lg ${theme.shadow} active:scale-[0.98]`
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
            >
              <Download size={20} />
              선택 기간 다운로드
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
